/// <amd-module name="Wiki/WitQuerySelector/WitQuerySelector" />
import * as React from "react";

import { announce } from "VSS/Core/Util/Accessibility";
import { ICancelablePromise } from "VSS/Core/Util/Promise";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FilterBar } from "VSSUI/FilterBar";
import { Spinner } from "VSSUI/Spinner";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT, IFilterState } from "VSSUI/Utilities/Filter";

import { ITfsPageService } from "Tfs/Platform/Page";
import { QueryHierarchyItemsResult } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";

import { FilteredQueriesTable } from "Wiki/WitQuerySelector/FilteredQueriesTable";
import { getSharedQueries, QueryProperties, QueryTreeItem } from "Wiki/WitQuerySelector/Helper";
import { QueriesTree } from "Wiki/WitQuerySelector/QueriesTree";
import * as Resources from "Wiki/WitQuerySelector/Resources";

export interface IWitQuerySelectorProps {
    onQuerySelected: (queryId: string, queryProperties: QueryProperties, insertQuery?: boolean) => void;
    resetQuery: () => void;
    filterBarItemInputClassName?: string;
    className?: string;
    filter?: Filter; // used in UTs
}

export interface IWitQuerySelectorState {
    filterText?: string;
    filteringInProgress?: boolean;
    filteredQueries?: QueryTreeItem[];
}

export class WitQuerySelector extends VssComponent<IWitQuerySelectorProps, IWitQuerySelectorState> {
    private static readonly s_filterKey = "queryFilter";
    private _filter: Filter;
    private _projectId: string | undefined;
    private _witRestClient: IWorkItemTrackingRestClient;
    private _filteringQueriesPromise: ICancelablePromise<QueryHierarchyItemsResult>;

    constructor(props: IWitQuerySelectorProps, context: IVssComponentContext) {
        super(props, context);

        this._filter = props.filter || new Filter();

        const tfsPageData = context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        this._projectId = tfsPageData && tfsPageData.project && tfsPageData.project.id;
        this._witRestClient = getWorkItemTrackingClient(context.pageContext);
        this.state = {};
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._filter.subscribe(this._onFilterUpdate, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._filter.unsubscribe(this._onFilterUpdate, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element | null {
        const { filterText, filteringInProgress, filteredQueries } = this.state;
        if (!this._projectId) {
            return null;
        }

        let filteredContent: JSX.Element | null = null;
        if (filterText) {
            if (filteringInProgress) {
                filteredContent = <Spinner label={Resources.FilteringQueriesSpinnerLabel} />;
            } else if (filteredQueries) {
                filteredContent = <FilteredQueriesTable filteredQueries={filteredQueries} onQuerySelected={this.props.onQuerySelected} />;
            }
        }

        return (
            <div className={css("witq-selector", this.props.className)}>
                <FilterBar filter={this._filter} className="query-filterbar">
                    <KeywordFilterBarItem
                        inputClassName={this.props.filterBarItemInputClassName}
                        placeholder={Resources.FilterBarPlaceHolderText}
                        filterItemKey={WitQuerySelector.s_filterKey}
                    />
                </FilterBar>
                {filteredContent}
                <div className={css(filterText && "hidden")}>
                    <QueriesTree projectId={this._projectId} onQuerySelected={this.props.onQuerySelected} resetQuery={this.props.resetQuery} />
                </div>
            </div>
        );
    }

    private _onFilterUpdate = (newValue: IFilterState, action: string): void => {
        if (newValue.hasOwnProperty(WitQuerySelector.s_filterKey)) {
            const filterItemState = newValue[WitQuerySelector.s_filterKey];
            const filterText: string = filterItemState ? filterItemState.value : "";

            if (this.state.filterText && !filterText) {
                this._filter.reset();
                this.props.resetQuery();
                this.setState({
                    filterText: undefined,
                    filteringInProgress: false,
                    filteredQueries: undefined
                });

                return;
            }

            if (this.state.filterText && localeIgnoreCaseComparer(this.state.filterText, filterText) === 0) {
                return;
            }

            if (filterText && this._projectId) {
                this.props.resetQuery();
                this.setState({ filterText, filteringInProgress: true });

                if (this._filteringQueriesPromise) {
                    this._filteringQueriesPromise.cancel(); // cancel the existing promise before firing a new one.
                }

                this._filteringQueriesPromise = this.trackPromise(this._witRestClient.searchQueries(this._projectId, filterText, 50));
                this._filteringQueriesPromise.promise.then(filteredItemsResult => {
                    const filteredQueries = getSharedQueries(filteredItemsResult.value);
                    _announceFilterResults(filteredQueries.length);
                    this.setState({ filterText, filteredQueries, filteringInProgress: false });
                });
            }
        }
    };
}

// export for UTs
export function _announceFilterResults(resultsCount: number): void {
    if (resultsCount === 0) {
        announce(Resources.NoQueriesFound);
    } else if (resultsCount === 1) {
        announce(Resources.Found1Query);
    } else {
        announce(format(Resources.FoundNQueries, resultsCount));
    }
}
